import { type PayrollProviderConfig } from 'utils/web_api.schemas'

import { type OnFailureCallback, type OnSuccessCallback } from './PayrollConnectionPicker'
import { useAdpIntegration } from '../hooks/useAdpIntegration'
import { useFinchIntegration } from '../hooks/useFinchIntegration'
import { useKomboIntegration } from '../hooks/useKomboIntegration'
import { useMergeIntegration } from '../hooks/useMergeIntegration'

// Constants for API connection methods
export const API_CONNECTION_METHOD = {
  FINCH: 'finch',
  MERGE_DEV: 'merge_dev',
  KOMBO: 'kombo',
  ADP: 'adp',
}

interface ProviderStrategy {
  startConnection: (providerConfig: PayrollProviderConfig, supportsAutomatedDeductions?: boolean) => Promise<void>;
  openModal: () => void;
  isModalReady: () => boolean;
  cleanup: () => void;
}

interface StrategyOptions {
  /**
   * A callback that will be called when non-oauth connections are successful.
   * It will be called with an argument detailing the capabilities of the
   * provider so the callback can conditionally decide if, where, and how to
   * redirect the user.
   */
  onSuccessCallback: OnSuccessCallback;
  /**
   * For integrations like ADP use OAuth, we need to provide a URL to redirect
   * to after the OAuth flow is complete.
   */
  onSuccessUrl: string;
  /**
   * A callback that will be called when non-oauth connections are unsuccessful.
   * It will be called to notify the user of the failure.
   */
  onFailureCallback?: OnFailureCallback;
  /**
   * For integrations like ADP use OAuth, we need to provide a URL to redirect
   * to if the OAuth flow fails.
   */
  onFailureUrl?: string;
}

export function createPayrollProviderStrategies ({
  onSuccessCallback,
  onSuccessUrl,
  onFailureUrl,
}: StrategyOptions): Record<string, ProviderStrategy> {
  const finchIntegration = useFinchIntegration({ onSuccessCallback })
  const mergeIntegration = useMergeIntegration({ onSuccessCallback })
  const komboIntegration = useKomboIntegration({ onSuccessCallback })
  const adpIntegration = useAdpIntegration({ onSuccessUrl, onFailureUrl })

  return {
    [API_CONNECTION_METHOD.FINCH]: {
      startConnection: (providerConfig: PayrollProviderConfig, supportsAutomatedDeductions = false) =>
        finchIntegration.startConnection(providerConfig.id, supportsAutomatedDeductions),
      openModal: finchIntegration.openModal,
      isModalReady: () => !!finchIntegration.sessionId && !!finchIntegration.providerIdToOpen,
      cleanup: () => finchIntegration.setProviderIdToOpen(undefined)
    },
    [API_CONNECTION_METHOD.MERGE_DEV]: {
      startConnection: (providerConfig: PayrollProviderConfig) => mergeIntegration.startConnection(providerConfig),
      openModal: mergeIntegration.openModal,
      isModalReady: () => {
        // For shared connections, show modal when we have a pending connection and no modal container yet
        if ((mergeIntegration as any).pendingSharedConnection) {
          return !mergeIntegration.modalContainer
        }
        // For regular connections, use the existing logic
        return !!mergeIntegration.linkToken &&
               !!mergeIntegration.providerIdToOpen &&
               mergeIntegration.isReady
      },
      cleanup: () => mergeIntegration.setProviderIdToOpen(undefined)
    },
    [API_CONNECTION_METHOD.KOMBO]: {
      startConnection: (providerConfig: PayrollProviderConfig) => komboIntegration.startConnection(providerConfig.id),
      openModal: komboIntegration.openModal,
      isModalReady: () => !!komboIntegration.komboLink && !!komboIntegration.providerIdToOpen,
      cleanup: () => komboIntegration.setProviderIdToOpen(undefined)
    },
    [API_CONNECTION_METHOD.ADP]: {
      startConnection: (providerConfig: PayrollProviderConfig) => {
        return adpIntegration.startConnection(providerConfig.id)
      },
      openModal: () => {
        return adpIntegration.openModal()
      },
      isModalReady: () => {
        // ADP is ready when we have a provider to open AND no modal container yet
        // Once the modal is opened, this should return false so useEffect doesn't interfere
        return !!adpIntegration.providerIdToOpen &&
                     !adpIntegration.modalContainer
      },
      cleanup: () => {
        return adpIntegration.cleanup()
      }
    }
  }
}
